<template>
	<div>
		<form-title title="My Prizes">
			Here you will find a list of all your prizes.
		</form-title>


		<!-- Prizes Search -->
		<v-row>
			<v-col cols="6" md="4">

				<v-text-field
						v-model="searchPrizes"
						append-icon="mdi-magnify"
						class="rounded-lg"
						clearable
						dense
						hide-details
						label="Search"
						outlined
				></v-text-field>

			</v-col>
			<v-col cols="6" md="3">
				<v-select
						v-model="fulfillerFilter"
						class="rounded-lg"
						dense
						hide-details
						:items="allFulfillers"
						label="Filter By Fulfiller"
						multiple
						outlined
				/>
			</v-col>
      <v-col cols="6" md="3">
        <div class="form-check form-check-inline">
          <input
              v-model="showDeleted"
              class="form-check-input"
              type="checkbox"
              :value="1"
              false-value=""
          />
          <label class="form-check-label"
          >Archived</label
          >
        </div>
      </v-col>
		</v-row>
		<!-- Prizes Search - END -->

		<bw-table
				aria-label="data-table"
				:headers="headers"
				:items="filteredPrizes"
				hide-row-select
				hide-search
				:search="searchPrizes"
		>
			<template v-slot:no-data>
				Oops, nothing added yet! Click Create New Prize in order to use Prizes in your
				pass build.
			</template>
			<template v-slot:item.image="{ item: prize }">
				<div class="flex-nowrap">
					<v-img
							:key="`prize-${prize.id}-image`"
							aspect-ratio="2"
							cover
							class="rounded"
							lazy-src="https://placehold.co/400x300?text=Loading"
							max-width="120"
							:src="prize.media[0] && prize.media[0].url || '/images/placehold.co.png'"
							:width="$vuetify.breakpoint.smAndUp ? '10vw' : '20vw'"
					></v-img>
					<v-chip
							:color="prize.deleted ? 'info' : prize.active ? 'primary' : 'warning lighten-1'"
							class="float-right rounded-pill mt-n2"
							label
							light
							small
					>
						<span class="white--text">
							{{ prize.deleted ? 'Archived' : prize.active ? 'Active' : 'Inactive' }}
						</span>
					</v-chip>
				</div>
			</template>
			<template v-slot:item.name="{ item: prize }">
				<div class="text-subtitle-2">
					{{ prize.name }}
				</div>
			</template>
			<template v-slot:item.highlights="{ item: prize }">
				<div class="text-subtitle-2">
					<span  v-html="prize.highlights"></span>
				</div>
			</template>
			<template v-slot:item.inventory="{ item: prize }">
				<div style="min-width: 120px;">
					<div
							v-for="({
							attributeName,
							quantity,
							remaining,
							remainingColor
						}, idx) in prize.inventory"
							:key="idx"
							class="d-flex"
					>
						<template v-if="quantity || quantity === 0">
							<div v-if="attributeName !== ''">{{ attributeName }}</div>
							<div v-else-if="prize.attributes.length">Any Attribute</div>
							<div
									:class="attributeName ? 'pl-1 ml-auto d-flex align-center' : ''"
									:style="{ color: remainingColor }"
							>
								{{ `${remaining}/${quantity}` }}
							</div>
						</template>
						<template v-else>
							<div v-if="attributeName !== ''">{{ attributeName }}</div>
							<div v-else-if="prize.attributes.length">Any Attribute</div>
							<div
									:class="attributeName || prize.attributes.length ? 'pl-1 ml-auto d-flex align-center' : ''"
									title="Unlimited"
							>
								Unlimited
							</div>
						</template>
					</div>
				</div>
			</template>
			<template v-slot:item.actions="{ item: prize }">
				<div class="d-flex justify-end">
					<v-btn
							color="secondary"
							depressed
							fab
							outlined
							small
							title="Edit"
							@click="editPrize(prize)"
					>
						<v-icon>
							mdi-pencil-outline
						</v-icon>
					</v-btn>
					<v-divider class="mx-2 my-1" vertical></v-divider>
					<v-btn
							color="secondary"
							depressed
							fab
							outlined
							small
							title="Clone"
							@click="duplicatePrize(prize)"
					>
						<v-icon>
							mdi-content-copy
						</v-icon>
					</v-btn>
					<v-divider class="mx-2 my-1" vertical></v-divider>
					<v-btn
              v-if="!prize.deleted"
							color="error"
							depressed
							fab
							outlined
							small
							title="Remove"
							@click="showRemovePrize(prize)"
					>
						<v-icon>
							mdi-trash-can-outline
						</v-icon>
					</v-btn>
          <v-btn
              v-else
              color="info"
              depressed
              fab
              outlined
              small
              title="Unarchive"
              @click="showConfirmRecoverPrize(prize)"
          >
            <v-icon>
              mdi-undo-variant
            </v-icon>
          </v-btn>
				</div>
			</template>
		</bw-table>
        <confirm-dialog
            description="Are your sure?"
            :open="!!itemToRecover"
            title="Recover Prize"
            @cancel="() => itemToRecover = null"
            @confirm="() => unremovePrize()"
        ></confirm-dialog>
		<v-dialog
				v-if="editingPrizeTitle"
				content-class="rounded-xl"
				persistent
				scrollable
				:value="true"
				width="800px"
		>
			<v-form
					v-model="formIsValid.editingPrize"
					ref="editingPrizeForm"
			>
				<v-card class="position-relative" rounded="xl">
					<v-card-title class="d-flex justify-space-between ma-0 ma-md-4">
						<div class="w-100">
							<div class="align-center d-flex justify-space-between">
								<h6>{{ editingPrizeTitle }}</h6>
							</div>
						</div>
					</v-card-title>
					<v-card-text class="text-body-2">
						<p class="caption mt-n2">Recommended minimum size: 1280 x 640 @ 2X</p>
						<v-row>
							<bw-image
									v-model="editingPrizeHero"
									:aspect-ratio="calculateAR(editingPrizeHero.cropperSettings.limitAR)"
									class="rounded-lg"
									:upload-assignment="'product'"
									:upload-assignment-id="editingPrize.id"
									:key="prizesMediaKey"
							></bw-image>
						</v-row>

						<v-row>
							<v-col>
								<v-text-field
										v-model="editingPrize.name"
										class="rounded-lg required"
										hide-details="auto"
										label="Prize Name"
										outlined
										type="text"
										:rules="rules.name"
								></v-text-field>
							</v-col>
						</v-row>

						<v-row>
							<v-col>
								<wysiwyg-input
									v-model="editingPrize.highlights"
									:counter="1000"
									hide-details="auto"
									label="Prize Highlights"
									outlined
									persistent-hint
									rows="4"
								/>
							</v-col>
						</v-row>

						<v-row v-if="showAnyQuantity">
							<v-col>
								<v-text-field
										v-model.number="editingPrize.inventory[0].used"
										class="rounded-lg"
										disabled
										hide-details="auto"
										hide-spin-buttons
										label="Quantity Claimed"
										outlined
										type="number"
										@wheel.native.prevent
								></v-text-field>
							</v-col>
							<v-col>
								<v-text-field
										v-model.number="editingPrize.inventory[0].remaining"
										class="rounded-lg"
										hide-details="auto"
										hide-spin-buttons
										label="Quantity Available"
										outlined
										:rules="rules.quantityAvailable"
										type="number"
										@wheel.native.prevent
								></v-text-field>
							</v-col>
						</v-row>
						<v-row>
							<v-col>
								<bw-date-time-picker
										v-model="editingPrize.active_dates[0].start_date"
										hide-details="auto"
										label="Active From"
										:max="editingPrize.active_dates[0].end_date"
										:tz="partner.timezone"
								></bw-date-time-picker>
							</v-col>
							<v-col>
								<bw-date-time-picker
										v-model="editingPrize.active_dates[0].end_date"
										hide-details="auto"
										label="Active Until"
										:min="editingPrize.active_dates[0].start_date"
										:tz="partner.timezone"
								></bw-date-time-picker>
							</v-col>
						</v-row>

						<form-divider mt="8"/>

						<form-title title="Attributes">
							Some prizes may have selectable attributes like size or color.

							<template v-slot:button>
								<v-btn
										class="mt-1 rounded-lg"
										color="info"
										depressed
										outlined
										small
										@click="addAttributeTitle = 'Add Attribute'"
								>
									<v-icon>
										mdi-plus
									</v-icon>
									Add Attribute
								</v-btn>
							</template>
						</form-title>

						<form-divider inner/>

						<ui-box no-padding>

							<template v-if="editingPrize.attributes.length === 0">

								<div
										class="align-center d-flex justify-center w-100"
										style="height: 80px;"
								>
									No attributes for this prize yet.
								</div>

							</template>

							<template v-else>

								<v-container
										v-if="$vuetify.breakpoint.mdAndUp"
										style="line-height: 1rem;"
								>
									<v-row
											no-gutters
											class="font-weight-bold grey--text mb-0 text-caption text--darken-2"
									>
										<v-col cols="auto" style="min-width: 30px;"></v-col>
										<v-col class="flex-grow-1">
											<v-row>
												<v-col class="flex-grow-1">Attribute Name</v-col>
											</v-row>
										</v-col>
										<v-col
												class="d-flex"
												cols="auto"
												style="min-width: 200px;"
										>
											<v-row>
												<v-col class="flex-grow-1">Inv. Limit</v-col>
											</v-row>
										</v-col>
										<v-col
												class="d-flex"
												cols="auto"
												style="min-width: 73px;"
										>
											Action
										</v-col>
									</v-row>
								</v-container>

								<v-divider
										v-if="$vuetify.breakpoint.mdAndUp"
										class="opacity-80"
								></v-divider>

								<v-container style="line-height: 1rem;">
									<draggable
											v-model="editingPrize.inventory"
											class="my-n4 sortables"
											direction="vertical"
											filter="input"
											tag="div"
											@start="onDragStart"
											@end="onDragAttributeEnd"
									>
										<transition-group
												type="transition"
												:name="!drag ? 'flip-list' : null"
										>
											<v-row
													v-for="(inventoryItem, index) in editingPrize.inventory"
													:key="`prize-inventory-${inventoryItem.attributeId}`"
													class="ma-0"
													no-gutters
											>
												<v-col cols="auto" style="min-width: 30px;">
													<v-icon
															:style="{
																marginTop: $vuetify.breakpoint.smAndDown ? 7 : 16
															}"
													>
														mdi-drag-vertical
													</v-icon>
												</v-col>
												<v-col class="flex-grow-1">
													<v-text-field
															v-model="inventoryItem.attributeName"
															class="rounded-lg"
															:dense="$vuetify.breakpoint.smAndDown"
															disabled
															hide-details="auto"
															hide-spin-buttons
															label="Name"
															outlined
															@wheel.native.prevent
													></v-text-field>
												</v-col>
												<v-col
														class="d-flex ms-4"
														cols="auto"
												>
													<v-tooltip bottom color="info" open-delay="250">
														<template v-slot:activator="{ on }">
															<v-text-field
																	v-model.number="inventoryItem.remaining"
																	class="rounded-lg"
																	:dense="$vuetify.breakpoint.smAndDown"
																	hide-details="auto"
																	hide-spin-buttons
																	label="Inv. Limit"
																	outlined
																	:rules="rules.remaining"
																	type="number"
																	v-on="on"
																	@wheel.native.prevent
															></v-text-field>
														</template>
														<span>Leaving this field blank<br/>means that there is<br/>unlimited availability.</span>
													</v-tooltip>
												</v-col>
												<v-col
														class="d-flex justify-end"
														cols="auto"
														style="min-width: 50px;"
												>
													<div
															class="d-flex ms-4"
															:style="{
																height: 28,
																marginTop: $vuetify.breakpoint.smAndDown ? 5 : 14
															}"
													>
														<v-btn
																color="secondary"
																depressed
																:disabled="!!inventoryItem.attributeId || inventoryItem.isAnyAttribute"
																fab
																outlined
																small
																@click="editAttribute(index)"
														>
															<v-icon>
																mdi-pencil-outline
															</v-icon>
														</v-btn>
														<v-divider class="mx-2" vertical></v-divider>
														<v-btn
																v-if="!!inventoryItem.attributeId || inventoryItem.isAnyAttribute"
																color="error"
																depressed
																fab
																outlined
																small
																@click="removeAttribute(inventoryItem.attributeId)"
														>
															<v-icon>
																mdi-trash-can-outline
															</v-icon>
														</v-btn>
														<v-btn
																v-else
																color="error"
																depressed
																fab
																outlined
																small
																@click="removeNewAttribute(index)"
														>
															<v-icon>
																mdi-trash-can-outline
															</v-icon>
														</v-btn>
													</div>
												</v-col>
											</v-row>
										</transition-group>
									</draggable>
								</v-container>
							</template>
						</ui-box>

						<form-divider/>

						<form-title title="Fulfillment">
							Prizes may need to be shipped, picked up or allow the option.
						</form-title>

						<form-divider inner/>

						<v-row>
							<v-col>
								<v-select
										v-model="editingPrize.settings.fulfiller"
										class="rounded-lg required"
										hide-details="auto"
										:items="[{name: 'Client', value: 'Client'}, {name: 'Printful', value: 'Printful'}, {name: 'Gooten', value: 'Gooten'}]"
										item-text="name"
										label="Fulfiller"
										outlined
										:rules="rules.fulfiller"
								></v-select>
							</v-col>
						</v-row>

						<v-row>
							<v-col>
								<v-text-field
										v-if="['Gooten', 'Printful'].includes(editingPrize.settings.fulfiller)"
										v-model="editingPrize.settings.fulfiller_sku"
										class="rounded-lg"
										hide-details="auto"
										label="Fulfiller SKU"
										outlined
										type="text"
								></v-text-field>
							</v-col>
						</v-row>

						<form-divider/>

						<form-title title="Delivery Method">
							Prizes may need to be shipped, picked up or allow the option.
						</form-title>

						<form-divider inner/>

						<v-row>
							<v-col>
								<v-select
										v-model="deliveryMethod"
										class="rounded-lg required"
										hide-details="auto"
										:items="[
											{ name: 'Shipped', value: 'shipped' },
											{ name: 'Picked Up', value: 'picked_up' },
											{ name: 'Shipped or Picked Up', value: 'shipped_or_picked_up'}
										]"
										item-text="name"
										label="Delivery Method"
										outlined
										:rules="rules.delivery_method"
										@change="deliverMethodeChanged"
								></v-select>
							</v-col>
						</v-row>
						<v-row>
							<v-col>
								<wysiwyg-input
										v-if="deliveryMethod === 'shipped' || deliveryMethod === 'shipped_or_picked_up'"
										v-model="editingPrize.shipping_details"
										:counter="1000"
										hide-details="auto"
										label="Shipping Details"
										outlined
										rows="4"
										:rules="rules.shippingDetails"
								></wysiwyg-input>
							</v-col>
						</v-row>
						<v-row>
							<v-col>
								<wysiwyg-input
										v-if="deliveryMethod === 'picked_up' || deliveryMethod === 'shipped_or_picked_up'"
										v-model="editingPrize.pickup_instructions"
										:counter="2000"
										hide-details="auto"
										label="Pickup Instructions"
										outlined
										rows="4"
								></wysiwyg-input>
							</v-col>
						</v-row>

					</v-card-text>

					<v-card-actions>
						<div class="align-center d-flex justify-space-between w-100">
							<span></span>
							<div class="d-flex justify-end">
								<v-btn
										class="me-2 me-md-4 rounded-lg"
										color="grey darken-1"
										depressed
										:large="$vuetify.breakpoint.mdAndUp"
										text
										@click="hideAndCleanEditingPrize"
								>
									Cancel
								</v-btn>
								<v-btn
										class="rounded-lg"
										color="primary"
										depressed
										:disabled="!validAddPrizeForm || isSaving"
										:large="$vuetify.breakpoint.mdAndUp"
										@click="savePrize"
								>
									Save Prize
								</v-btn>
							</div>
						</div>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>
		<v-dialog
				v-if="!!addAttributeTitle"
				content-class="rounded-xl"
				persistent
				scrollable
				:value="true"
				:width="showNewAttribute ? 600 : 400"
		>
			<v-form v-model="formIsValid.newAttribute">
				<v-card class="position-relative" rounded="xl">
					<v-card-title class="d-flex justify-space-between ma-0 ma-md-4">
						<div class="w-100">
							<div class="align-center d-flex justify-space-between">
								<h6>{{ addAttributeTitle }}</h6>
							</div>
						</div>
					</v-card-title>
					<v-card-text class="text-body-2">

						<v-row v-if="!showNewAttribute">
							<v-col>
								<v-select
										v-model="partnerAttributeSelect"
										class="rounded-lg"
										hide-details="auto"
										:items="partnerAttributesAvailable"
										item-text="name"
										item-value="id"
										label="Current attributes"
										outlined
										:rules="rules.partner_attribute_select"
								></v-select>
							</v-col>
						</v-row>

						<template v-if="showNewAttribute">
							<v-row>
								<v-col cols="12" md="6">
									<v-text-field
											v-model="newAttribute.name"
											class="rounded-lg required"
											hide-details="auto"
											hint="Ex: Adult"
											label="Name"
											outlined
											persistent-hint
											type="text"
											:rules="rules.attribute_name"
									></v-text-field>
								</v-col>
								<v-col cols="12" md="6">
									<v-text-field
											v-model="newAttribute.sub_title"
											class="rounded-lg"
											hide-details="auto"
											hint="Ex: Age 21 and older"
											label="Subtitle"
											outlined
											persistent-hint
											type="text"
									></v-text-field>
								</v-col>
							</v-row>
							<v-row>
								<v-col>
									<v-textarea
											v-model="newAttribute.description"
											class="rounded-lg"
											hide-details="auto"
											label="Description"
											outlined
											rows="2"
									></v-textarea>
								</v-col>
							</v-row>
						</template>
					</v-card-text>
					<v-card-actions>
						<div class="align-center d-flex justify-space-between w-100">
							<div>
								<v-btn
										v-if="!showNewAttribute"
										class="me-2 me-md-4"
										color="success"
										depressed
										fab
										outlined
										@click="showNewAttributeDialog"
								>
									<v-icon>mdi-plus-thick</v-icon>
								</v-btn>
								<v-btn
										v-else
										class="me-2 me-md-4"
										color="error"
										depressed
										fab
										outlined
										@click="showNewAttribute = false"
								>
									<v-icon>mdi-arrow-u-left-top-bold</v-icon>
								</v-btn>
							</div>
							<div class="d-flex justify-end">
								<v-btn
										class="me-2 me-md-4 rounded-lg"
										color="grey darken-1"
										depressed
										:large="$vuetify.breakpoint.mdAndUp"
										text
										@click="hideAndCleanAttributes"
								>
									Cancel
								</v-btn>
								<v-btn
										v-if="newAttribute.id || !newAttribute.hasOwnProperty('index')"
										class="rounded-lg"
										color="primary"
										depressed
										:disabled="!formIsValid.newAttribute"
										:large="$vuetify.breakpoint.mdAndUp"
										@click="saveAttribute"
								>
									Add
								</v-btn>
								<v-btn
										v-else
										class="rounded-lg"
										color="primary"
										depressed
										:disabled="!formIsValid.newAttribute"
										:large="$vuetify.breakpoint.mdAndUp"
										@click="updateAttribute"
								>
									Update
								</v-btn>
							</div>
						</div>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>

		<v-dialog
				v-if="!!prizeToRemove"
				content-class="rounded-xl"
				persistent
				scrollable
				:value="true"
				width="400px"
		>
			<v-form>
				<v-card class="position-relative" rounded="xl">
					<v-card-title class="d-flex justify-space-between ma-0 ma-md-4">
						<div class="w-100">
							<div class="align-center d-flex justify-space-between">
								<h6>Remove Prize</h6>
							</div>
						</div>
					</v-card-title>
					<v-card-text class="text-body-2">
						<v-row>
							<v-col>
								<div>
                                    Are you sure you want to remove <strong>{{ prizeToRemove.name }}</strong>?
                                    While you can unarchive this later, you'll need to manually re-add it to all passes.
								</div>
								<v-divider class="my-6"></v-divider>
								<div class="text-subtitle-2 mt-2">
									The prize will also be removed from:
								</div>
								<div class="mt-2">• Passes</div>
								<div class="ml-3">
									<div v-if="!!prizeToRemove.items && !!prizeToRemove.items.deals.length">
										<div v-for="(pass, i) in prizeToRemove.items.deals" :key="i">
											<a :href="`/partner-portal/${partner.id}/passes/${pass.id}/gamification`">{{
													pass.name
												}}</a>
										</div>
									</div>
									<div v-else>
										This prize wasn't added to any pass
									</div>
								</div>
								<div class="mt-2">• Channels</div>
								<div class="ml-3">
									<div
											v-if="!!prizeToRemove.items && !!prizeToRemove.items.offers.length">
										<div v-for="(channel, i) in prizeToRemove.items.offers" :key="i">
											<a :href="`https://bandwango.ddev.site/partner-portal/${partner.id}/channels/${channel.id}`">
												{{ channel.name }}
											</a>
										</div>
									</div>
									<div v-else>
										This prize wasn't added to any channel
									</div>
								</div>
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-actions>
						<div class="align-center d-flex w-100">
							<v-btn
									class="me-2 me-md-4 rounded-lg ml-auto"
									color="grey darken-1"
									depressed
									:large="$vuetify.breakpoint.mdAndUp"
									text
									@click="prizeToRemove = null"
							>
								Cancel
							</v-btn>
							<v-btn
									class="rounded-lg"
									color="primary"
									depressed
									:disabled="!prizeToRemove.items"
									:large="$vuetify.breakpoint.mdAndUp"
									@click="removePrize"
							>
								Confirm
							</v-btn>
						</div>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>
	</div>
</template>

<script>
import { calculateAR } from "../../../../Shared/helpers";
import ConfirmDialog from "../../../../Shared/UI/ConfirmDialog.vue";
import WysiwygInput from "../../../../Shared/Forms/WysiwygInput/index.vue";

export default {
	name: 'Prizes',
    components: {WysiwygInput, ConfirmDialog},
	props: {
		extraData: {
			type: Object,
			default: () => ({})
		},
		prizes: {
			type: Array,
			default: () => []
		},
		partner: {
			type: Object,
			default: () => ({})
		}
	},
	data() {
		return {
			addAttributeTitle: '',
			dateFormat: "YYYY-MM-DD",
			drag: false,
			editingPrize: null,
			editingPrizeTitle: '',
			fulfillerFilter: [],
			formIsValid: {
				customPoints: false,
				defaultPoints: false,
				expirePoints: false,
				newAttribute: false,
				editingPrize: false,
				prizes: false,
				updateAttribute: false
			},
			headers: [
				{
					align: 'center',
					mandatory: true,
					text: 'Image',
					value: 'image',
				},
				{
					align: 'start',
					mandatory: true,
					text: 'Name',
					value: 'name'
				},
				{
					align: 'start',
					mandatory: true,
					text: 'Highlights',
					value: 'highlights',
				},
				{
					align: 'start',
					mandatory: true,
					text: 'Inventory Limit',
					value: 'inventory',
				},
				{
					align: 'start',
					mandatory: true,
					text: 'Fulfiller',
					value: 'fulfiller',
				},
				{
					align: 'start',
					mandatory: true,
					text: 'Delivery Method',
					value: 'delivery',
				},
				{
					align: 'start',
					mandatory: true,
					sortable: false,
					text: 'Actions',
					value: 'actions',
					width: '1%'
				}
			],
			isSaving: false,
            itemToRecover: null,
			localPrizes: [],
			newAttribute: new Attribute(),
			prizesMediaKey: 0,
			redirectUrl: '',
			prizeToRemove: null,
			rules: {
				name: [v => !!v || 'Name is required.'],
				attribute_name: [
					v => !!v || 'Attribute name is required.',
					v => {
						if(!v) return true; // If it's empty, the first rule will catch it.

						const foundInPartnerAttributes = this.extraData.partnerAttributes.find(attribute => attribute.name === v);
						const foundInNewAttributes = this.editingPrize.attributes.find(attribute => attribute.name === v) && !this.editingAttribute?.name === v;

						return (foundInPartnerAttributes || foundInNewAttributes)
								? 'Attribute name already exists.'
								: true;
					}
				],
				delivery_method: [v => !!v || 'Delivery method is required.'],
				partner_attribute_select: [v => !!v || 'Attribute is required.'],
				fulfiller: [v => !!v || 'Fulfiller is required.'],
				highlights: [
					v => !v || v && v.length <= 1000 || 'Highlights must be less than 1000 characters',
				],
				remaining: [
					v => v === '' || (Number.isInteger(v) && v >= 0) || 'Quantity is not valid.'
				],
				shippingDetails: [
					v => !v || v && v.length <= 1000 || 'Details must be less than 1000 characters',
				],
				quantityAvailable: [
					v => !this.showAnyQuantity
							|| v === ''
							|| v === undefined
							|| (Number.isInteger(v) && v >= 0)
							|| 'Quantity is not valid.'
				]
			},
			searchPrizes: '',
      showDeleted: false,
      showNewAttribute: false,
		};
	},
	created() {
		this.localPrizes = this.prizes;
		this.setupPrizes();
	},
	mounted() {
		const { clone, fromPass, prizeId } = this.extraData;

		if(!!fromPass) {
			const { id: partnerId } = this.partner;
			this.redirectUrl = `/partner-portal/${partnerId}/passes/${fromPass}/gamification`;
			if(!!prizeId) {
				const prize = this.localPrizes.find(({ id }) => id === parseInt(prizeId));
				if(!!clone) {
					this.duplicatePrize(prize);
				}
				else {
					this.editPrize(prize);
				}
			}
			else {
				this.addNewPrize();
			}
		}
	},
	computed: {
		allFulfillers() {
			const fulfillers = [];

			this.localPrizes.map(({ fulfiller }) => {
						if(!fulfiller || fulfillers.some(f => f === fulfiller)) return;

						fulfillers.push({ text: fulfiller, value: fulfiller });
					}
			);
			return fulfillers;
		},
		deliveryMethod: {
			get() {
				if(!this.editingPrize) return;

				if(this.editingPrize.shipped && this.editingPrize.picked_up)
					return 'shipped_or_picked_up';
				else if(this.editingPrize.shipped)
					return 'shipped';
				else if(this.editingPrize.picked_up)
					return 'picked_up';
				else
					return '';
			},
			set(value) {
				switch (value) {
					case 'shipped_or_picked_up':
						this.editingPrize.shipped = 1;
						this.editingPrize.picked_up = 1;
						this.editingPrize.redemption_validation = '';
						break;
					case 'shipped':
						this.editingPrize.shipped = 1;
						this.editingPrize.picked_up = 0;
						this.editingPrize.redemption_validation = '';
						break;
					case 'picked_up':
						this.editingPrize.shipped = 0;
						this.editingPrize.picked_up = 1;
						this.editingPrize.redemption_validation = '';
						break;
					default:
						this.editingPrize.shipped = 0;
						this.editingPrize.picked_up = 0;
						this.editingPrize.redemption_validation = '';
				}
			}
		},
		editingPrizeHero: {
			get() {
				if(!this.editingPrize) return;
				let m = this.editingPrize.media.find(x => x.location === 'Hero');
				if(!m) {
					return {
						location: 'Hero',
						name: 'Hero',
						cropperSettings: {
							limitAR: '2:1',
							x: 0,
							y: 0
						}
					};
				}
				return {
					...m,
					minDimensions: false,
					cropperSettings: m.cropperSettings || {
						limitAR: '2:1',
						x: 0,
						y: 0
					}
				};
			},
			set(v) {
				if(!this.editingPrize) return;
				const index = this.editingPrize.media.findIndex(x => x.location === 'Hero');
				if(index !== -1) {
					this.$set(this.editingPrize.media, index, {
						...this.editingPrize.media[index],
						...v,
						minDimensions: false,
						cropperSettings: {
							limitAR: '2:1',
							x: 0,
							y: 0
						}
					});
				}
			}
		},
		filteredPrizes() {
			const { fulfillerFilter: filter } = this;

			return this.localPrizes.filter(({ fulfiller }) => {
				return filter.length === 0 || filter.includes(fulfiller);
			}).filter(({ deleted }) => {
        return this.showDeleted ? true : !deleted;
      });
		},
		partnerAttributesAvailable() {
			return this.extraData.partnerAttributes.filter(attribute => {
				return !this.editingPrize.attributes.find(attr => attr.id === attribute.id);
			});
		},
		partnerAttributeSelect: {
			get() {
			},
			set(value) {
				const attribute = this.extraData.partnerAttributes.find(attribute => attribute.id === value);
				if(attribute) {
					this.newAttribute = { ...attribute };
					this.selectedPartnerAttributeName = attribute.name;
				}
			}
		},
		showAnyQuantity() {
			const { editingPrize: { attributes, inventory } } = this;

			return attributes.length === 0 && inventory.length;
		},
		validAddPrizeForm() {
			if(!this.editingPrize) return false;
			const validImage = this.editingPrize.media.length > 0;
			return validImage && this.formIsValid.editingPrize;
		}
	},
	methods: {
		addNewPrize() {
			this.editingPrize = new Prize({}, this.extraData);
			this.editingPrizeTitle = 'New Prize';

			this.deliverMethodeChanged();
		},
		calculateAR,
		deliverMethodeChanged() {
			if( !this.editingPrize?.id ) {
				if (this.deliveryMethod == 'shipped' || this.deliveryMethod == 'shipped_or_picked_up') {
					if( !this.editingPrize.shipping_details && this.extraData?.prizeDefaults?.shippingDetails ) {
						this.editingPrize.shipping_details = this.extraData.prizeDefaults.shippingDetails;
					}
				}

				if (this.deliveryMethod == 'picked_up' || this.deliveryMethod == 'shipped_or_picked_up') {
					if( !this.editingPrize.pickup_instructions && this.extraData?.prizeDefaults?.pickupInstructions ) {
						this.editingPrize.pickup_instructions = this.extraData.prizeDefaults.pickupInstructions;
					}
				}
			}
		},
		duplicatePrize(prize) {
			this.editingPrizeTitle = 'Duplicate Prize';
			const clonedPrize = cloneDeep(prize);
			delete clonedPrize.id;
			if(clonedPrize.media) {
				clonedPrize.media.forEach(media => {
					delete media.id;
					delete media.assignment_id;
				});
			}
			if(clonedPrize.tickets) {
				clonedPrize.tickets.forEach(ticket => {
					delete ticket.id;
					delete ticket.product_id;
					ticket.used = 0;
					ticket.quantity = 0;
				});
			}
			if(clonedPrize.attributes) {
				clonedPrize.attributes.forEach(attribute => {
					if(attribute.pivot) {
						delete attribute.pivot.product_id;
					}
				});
			}
			const newPrize = new Prize({
				...clonedPrize,
				name: '(Duplicated Of) ' + prize.name
			}, this.extraData);
			this.setupInventory(newPrize);
			// New tickets will be created from inventory on save
			newPrize.tickets = [];
			newPrize.id = '';
			this.prizesMediaKey += 1;

			this.editingPrize = newPrize;
		},
		editAttribute(inventoryIndex) {
			const index = this.editingPrize.inventory?.[inventoryIndex]?.attributesIndex;
			this.newAttribute = { ...this.editingPrize.attributes[index] };
			this.showNewAttribute = true;
			this.addAttributeTitle = 'Edit Attribute';
		},
		editPrize(prize) {
			this.editingPrizeTitle = 'Edit Prize';
			this.editingPrize = new Prize(prize, this.extraData);
			this.prizesMediaKey += 1;
		},
		getRemaining(quantity, used) {
			if(quantity === null) {
				return { remaining: '', remainingColor: 'inherit' }
			}

			const remaining = quantity - used;
			const remainingColor = remaining <= 0.2 * quantity
					? 'var(--v-error-base)'
					: 'inherit';

			return { remaining, remainingColor }
		},
		hideAndCleanAttributes() {
			this.addAttributeTitle = '';
			this.newAttribute = new Attribute();
			this.showNewAttribute = false;
		},
		hideAndCleanEditingPrize(redirect = false) {
			if(redirect && !!this.redirectUrl) {
				window.EventBus.$emit('system-messages', [
					{
						message: 'Redirecting...',
						messageType: 'success',
						showOverlay: true
					}
				]);
				window.location.href = this.redirectUrl;
				return;
			}
			this.editingPrize = new Prize({}, this.extraData);
			this.editingPrizeTitle = '';
			this.editingPrize = null;
			this.prizesMediaKey += 1;
		},
		onDragAttributeEnd() {
			this.drag = false;
		},
		onDragStart() {
			this.drag = true;
		},
		async refreshPrizes() {
			try {
				const response = await axios.get(`/partner-portal/${this.partner.id}/prizes/list`);
				this.localPrizes = response.data.allPrizes;
				this.setupPrizes();
				this.prizesTableKey += 1;
			}
			catch (error) {
				window.EventBus.$emit('system-messages', [
					{ message: error.message, messageType: 'error' }
				]);
			}
		},
		removeAttribute(attributeId) {
			const { attributes, inventory, tickets, ticketsRemoved } = this.editingPrize;
			const attributesIndex = attributes.findIndex(({ id }) => id === attributeId);
			const inventoryIndex = inventory.findIndex(({ attributeId: id }) => id === attributeId);
			const ticketsIndex = tickets.findIndex(({ attribute_id: id }) => id === attributeId);

			if(attributesIndex !== -1) {
				attributes.splice(attributesIndex, 1);
			}

			inventory.splice(inventoryIndex, 1);

			if(!inventory.length) {
				this.editingPrize.inventory = (new Prize({}, this.extraData)).inventory;
			}

			this.editingPrize.ticketsRemoved = ticketsRemoved.concat(tickets.splice(ticketsIndex, 1));
		},
		removeNewAttribute(attributesIndex) {
			const { attributes, inventory } = this.editingPrize;
			const inventoryIndex = inventory.findIndex(({ attributesIndex: index }) => index === attributesIndex);

			attributes.splice(attributesIndex, 1);
			inventory.splice(inventoryIndex, 1);
		},
		async removePrize() {
			this.isSaving = true;
			const {
				id: prizeId
			} = this.prizeToRemove;
			const url = `/partner-portal/${this.partner.id}/prizes/save-prize/${prizeId}`;
			try {
				const response = await axios.put(url, { deleted: 1 });
				const message = 'Prize removed. Retrieving prizes list...';
				window.EventBus.$emit('system-messages', [
					{
						message,
						messageType: 'success',
						showOverlay: true
					}
				]);
				await this.refreshPrizes();
			}
			catch (error) {
				window.EventBus.$emit('system-messages', [
					{ message: error.message, messageType: 'error' }
				]);
			}
			finally {
				this.prizeToRemove = null;
				this.isSaving = false;
			}
		},
		saveAttribute() {
			this.newAttribute.index = this.editingPrize.attributes.length;
			this.editingPrize.attributes.push(this.newAttribute);
			//remove all inventory items with no name
			this.editingPrize.inventory = this.editingPrize.inventory.filter(({ attributeName }) => !!attributeName);
			//push the new inventory item based on new attribute
			this.editingPrize.inventory.push({
				attributeId: this.newAttribute.id,
				attributeName: this.newAttribute.name,
				attributesIndex: this.newAttribute.index,
				quantity: '',
				remaining: '',
				ticketId: undefined
			});

			this.hideAndCleanAttributes();
		},
		async savePrize() {
			this.isSaving = true;
			const {
				active_dates: [{ end_date, start_date }],
				attributes,
				id: prizeId = '',
				inventory,
				tickets,
				ticketsRemoved
			} = this.editingPrize;
			const url = `/partner-portal/${this.partner.id}/prizes/save-prize/${prizeId}`;

			let sortOrder = 1;
			inventory.forEach(
					({
						attributeId,
						attributeName,
						attributesIndex,
						remaining,
						ticketId,
						used = 0
					}) => {
						if(!!ticketId) {
							const ticket = tickets.find(({ id }) => id === ticketId);
							ticket.quantity = remaining === ''
									? null
									: parseInt(remaining) + parseInt(used);
						}
						else if(remaining !== 0 || attributeId === 0) {
							tickets.push({
								id: null,
								quantity: remaining,
								attribute_id: attributeId,
								attribute_name: attributeName
							});
						}

						if(attributeId !== 0) {
							const attribute = attributesIndex
									? attributes[attributesIndex]
									: attributes.find(({ id }) => id === attributeId)
							;

							attribute.sort_order = sortOrder++;
						}
					}
			);

			attributes.forEach(attribute => {
				attribute.attribute_id = attribute.id;
				this.$delete(attribute, 'id');
			});

			const active_dates = [
				{
					end_date: !!end_date ? end_date : '',
					start_date: !!start_date ? start_date : ''
				}
			];
			this.editingPrize.active_dates = active_dates;

			// delete tickets with removed attributes
			ticketsRemoved.map(ticketRemoved => {
				ticketRemoved.deleted = 1;
				tickets.push(ticketRemoved);
			});

			this.extraData.prizeDefaults = this.extraData.prizeDefaults || {};
			this.extraData.prizeDefaults.shippingDetails = this.editingPrize.shipping_details || this.extraData.prizeDefaults.shippingDetails;
			this.extraData.prizeDefaults.pickupInstructions = this.editingPrize.pickup_instructions || this.extraData.prizeDefaults.pickupInstructions;

			try {
				const response = await axios.put(url, this.editingPrize);
				let message = this.editingPrize.id
						? 'Prize updated.'
						: 'Prize created.';
				message = !!this.redirectUrl
						? `${message} Retrieving prizes list...`
						: `${message} Redirecting...`;

				window.EventBus.$emit('system-messages', [
					{
						message,
						messageType: 'success',
						showOverlay: true
					}
				]);

				this.updatePartnerAttributes(response.data);
				this.hideAndCleanEditingPrize(false);
				await this.refreshPrizes();
			}
			catch (error) {
				window.EventBus.$emit('system-messages', [
					{ message: error.message, messageType: 'error' }
				]);
			}
			finally {
				this.isSaving = false;
				if(this.redirectUrl) {
					window.location.href = this.redirectUrl;
				}
			}
		},
		async showRemovePrize(prize) {
			try {
				const { id: prizeId } = prize;
				const { id: partnerId } = this.partner;

				this.prizeToRemove = prize;

				const { data: items } = await axios.get(`/partner-portal/${partnerId}/prizes/get-items/${prizeId}`);
				this.$set(this.prizeToRemove, 'items', items);
			}
			catch (error) {
				this.prizeToRemove = null;
				window.EventBus.$emit('system-messages', [
					{ message: error.message, messageType: 'error' }
				]);
			}
		},
        showConfirmRecoverPrize(prize) {
            this.itemToRecover = {id: prize.id};
        },
    async unremovePrize() {
      this.isSaving = true;
      const prizeId = this.itemToRecover.id;
      const url = `/partner-portal/${this.partner.id}/prizes/save-prize/${prizeId}`;
      try {
        const response = await axios.put(url, { deleted: 0 });
        const message = 'Prize unarchived. Retrieving prizes list...';
        window.EventBus.$emit('system-messages', [
          {
            message,
            messageType: 'success',
            showOverlay: true
          }
        ]);
        await this.refreshPrizes();
      }
      catch (error) {
        window.EventBus.$emit('system-messages', [
          { message: error.message, messageType: 'error' }
        ]);
      }
      finally {
        this.itemToRecover = null;
        this.isSaving = false;
      }
    },
		setupInventory(prize) {
			const {
				attributes,
				tickets
			} = prize;

			const anyTicket = tickets.find(
					({ attribute_id: attributeId }) => attributeId === 0
			);
			const defaultTicket = { quantity: 0, used: 0 };

			// add anyTicket or []
			if(!anyTicket) {
				prize.inventory = [];
			}
			else {
				const { id: ticketId, quantity, used } = anyTicket;
				const { remaining, remainingColor } = this.getRemaining(quantity, used);
				prize.inventory = [
					{
						attributeId: 0,
						attributeName: 'Any',
						isAnyAttribute: true,
						quantity,
						remaining,
						remainingColor,
						ticketId,
						used
					}
				];
			}
			// push the attributes with their tickets or use default
			prize.inventory.push(
					...attributes.map(
							({ id: attributeId, name: attributeName }) => {
								const ticket = tickets.find(
										({ attribute_id: ticketAttributeId }) => ticketAttributeId === attributeId
								);

								const { id: ticketId, quantity, used } = ticket || defaultTicket;
								const { remaining, remainingColor } = this.getRemaining(quantity, used);
								return {
									attributeId,
									attributeName,
									quantity,
									remaining,
									remainingColor,
									ticketId,
									used
								};
							}
					)
			);
		},
		setupPrizes() {
			this.localPrizes.forEach(prize => {
				const {
					picked_up,
					settings,
					shipped,
				} = prize;

				this.setupInventory(prize);

				prize.fulfiller = settings?.fulfiller ?? '';

				let delivery = '';
				if(shipped) delivery = 'Shipped';
				if(picked_up) delivery = shipped ? `${delivery} or Picked Up` : 'Picked Up';

				prize.delivery = delivery;
			});
		},
		showNewAttributeDialog() {
			this.showNewAttribute = true;
			this.newAttribute = new Attribute();
		},
		updateAttribute() {
			const { attributes, inventory } = this.editingPrize;
			const { index, name } = this.newAttribute;
			const inventoryIndex = inventory.findIndex(({ attributesIndex }) => index === attributesIndex);

			attributes[index] = { ...this.newAttribute };
			inventory[inventoryIndex].attributeName = name;

			this.hideAndCleanAttributes();
		},
		updatePartnerAttributes({ prize }) {
			const { extraData: { partnerAttributes } } = this;
			const attributes = prize?.attributes;

			if(attributes) {
				attributes.map(attr => {
					if(!partnerAttributes.some(({ id }) => id === attr.id)) {
						partnerAttributes.push(attr);
					}
				});
			}
		}
	}
};

function Attribute() {
	this.attribute_id = null;
	this.description = null;
	this.name = null;
	this.sub_title = null;
	this.ticketQuantity = null;
}

function Prize(prize, extraData) {
	const {
		prizeTemplate: { id: prizeTemplateId },
		prizeStatus: { id: prizeStatusId }
	} = extraData;

	this.id = prize.id;

	this.template_id = prizeTemplateId;
	this.name = prize.name;
	if(prize?.media?.length) {
		this.media = [...prize.media];
	}
	else {
		this.media = [
			{
				location: 'Hero',
				name: 'Hero',
				url: '/images/placehold.co.png'
			}
		];
	}
	this.categories = prize?.categories?.length
			? [...prize.categories]
			: [extraData.prizeCategory.id];
	this.status = prize.status || prizeStatusId;
	this.highlights = prize.highlights;
	this.inventory = prize.inventory ? [...prize.inventory] : [
		{
			attributeId: 0,
			attributeName: '',
			quantity: ''
		}
	];
	this.attributes = prize?.attributes?.length ? [...prize.attributes] : [];
	this.redeem_on_dashboard = prize.redeem_on_dashboard ?? 1;
	this.redemptions = prize.redemptions || 'shipped';
	this.settings = {
		fulfiller: null,
		fulfiller_sku: null,
		...prize.settings
	};
	this.shipped = prize.shipped ?? 1;
	this.picked_up = prize.picked_up ?? 0;
	this.shipping_details = prize.shipping_details;
	this.pickup_instructions = prize.pickup_instructions;
	this.redemption_validation = prize.redemption_validation;
	this.ticketQuantity = prize.ticketQuantity;
	this.tickets = prize.tickets || [];
	this.ticketsRemoved = [];
	this.active_dates = prize.active_dates?.length ? [...prize.active_dates] : [
		{
			end_date: '',
			start_date: ''
		}
	];
	this.activeUntil = '';
	this.active = prize.active ?? true;
	this.fulfiller = prize.fulfiller;
	this.delivery = prize.delivery;
}
</script>

<style lang="scss">
.sortables > span > div {
	margin: 0 -1rem !important;
	padding: 1rem;

	&:hover {
		background-color: #f0f8f0 !important;
	}

	&:not(:first-child):not(.sortable-drag) {
		border-top: solid 1px rgba(0, 0, 0, 0.096)
	}
}
</style>
